/**
 * editMode.js — Master toggle for the editor panels.
 *
 * Press **Alt + Shift + E** anywhere in the app to show or hide the
 * three editors:
 *   - QuickNavEditor (Guided Tour edit) — main screen
 *   - NavHintsEditor (POI / NavHint)    — main screen
 *   - View360Selector admin edit mode   — inside a drone sky-view
 *
 * The flag is **session-only** (in-memory). Refreshing the page always
 * starts with edit mode OFF; the user must press Alt+Shift+E again to
 * re-enable it. A `window.toggleEditMode()` console helper is exposed.
 */
import { useEffect, useState } from 'react';

const EVENT_NAME = 'editModeChange';

let _active = false;   // in-memory only — never persisted
let _installed = false;

export function isEditModeActive() {
  return _active;
}

export function setEditMode(value) {
  const next = !!value;
  if (next === _active) return;
  _active = next;
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
    // eslint-disable-next-line no-console
    console.log(`🛠️  Edit mode ${next ? 'ON' : 'OFF'}`);
  } catch {}
}

export function toggleEditMode() {
  setEditMode(!_active);
}

/**
 * Install the global Alt+Shift+E shortcut. Safe to call multiple times
 * (idempotent). Should be invoked once from the app root.
 *
 * The Matterport iframe (served from /bundle/showcase.html, same-origin)
 * grabs keyboard focus as soon as the user clicks into the scene, so a
 * listener on the parent `window` alone never fires once they walk
 * around. We mirror the listener onto the iframe's own window/document
 * — and re-attach it whenever the iframe element is replaced — so the
 * shortcut keeps working from any focus state.
 */
export function installEditModeShortcut() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  const onKey = (e) => {
    if (
      (e.key === 'e' || e.key === 'E') &&
      e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey
    ) {
      e.preventDefault();
      e.stopPropagation();
      toggleEditMode();
    }
  };
  // Capture phase so the Matterport iframe can't swallow the event when
  // focus is on the parent document.
  window.addEventListener('keydown', onKey, true);
  document.addEventListener('keydown', onKey, true);

  // Mirror onto the Matterport iframe so the shortcut still fires when
  // the user has clicked into the 3D scene (iframe steals focus). The
  // iframe is same-origin (/bundle/showcase.html) so we can reach into
  // its contentDocument. Try repeatedly because the iframe is mounted
  // asynchronously by MatterportViewer and may also be re-created on
  // hot-reload.
  const wired = new WeakSet();
  const wireIframe = (iframe) => {
    if (!iframe || wired.has(iframe)) return;
    const attach = () => {
      try {
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!doc || !win) return;
        // contentDocument can flip between an empty doc and the loaded
        // one — guard against double-binding by tagging the document.
        if (doc.__editModeShortcutWired) return;
        doc.__editModeShortcutWired = true;
        doc.addEventListener('keydown', onKey, true);
        win.addEventListener('keydown', onKey, true);
        wired.add(iframe);
      } catch (_) {
        // Cross-origin or not-yet-ready — retry later.
      }
    };
    if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
      attach();
    }
    iframe.addEventListener('load', attach);
    // Retry a few times for the slow-loading case.
    let tries = 0;
    const t = setInterval(() => {
      attach();
      if (wired.has(iframe) || ++tries > 20) clearInterval(t);
    }, 500);
  };
  const scan = () => {
    const frames = document.querySelectorAll('iframe');
    frames.forEach(wireIframe);
  };
  // Initial scan + observe any iframes added later.
  scan();
  try {
    const obs = new MutationObserver(() => scan());
    obs.observe(document.body, { childList: true, subtree: true });
  } catch (_) { /* MutationObserver always available in modern browsers */ }

  // Console helper for debugging / support.
  try { window.toggleEditMode = toggleEditMode; } catch {}
}

/**
 * React hook — returns the current edit-mode flag and re-renders when
 * it changes.
 */
export function useEditMode() {
  const [active, setActive] = useState(_active);
  useEffect(() => {
    const onChange = (e) => setActive(!!e.detail);
    window.addEventListener(EVENT_NAME, onChange);
    setActive(_active);
    return () => window.removeEventListener(EVENT_NAME, onChange);
  }, []);
  return active;
}
